import {
  doc,
  getDoc,
  getDocs,
  collection,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  serverTimestamp,
} from 'firebase/firestore';
import { firebaseFirestore } from '@/infrastructure/firebase/firebaseFirestore.js';
import { type SuiteResult } from '@k-ucap/shared';
import { getCurrentEnvironment, buildConverter } from './firestoreUtils.js';
import type { SuiteResultRepository } from '@/infrastructure/repositories/RepositoryInterfaces.js';

const SUITE_RESULTS = 'suiteResults';

const converter = buildConverter<SuiteResult>(
  (data) => data as SuiteResult,
  (model) => ({ ...model, createdAt: serverTimestamp() })
);

export class FirestoreSuiteResultRepository implements SuiteResultRepository {
  async findAll(): Promise<SuiteResult[]> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const q = query(collection(db, SUITE_RESULTS), where('environment', '==', getCurrentEnvironment())).withConverter(
      converter
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data());
  }

  async findById(id: string): Promise<SuiteResult | undefined> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const snap = await getDoc(doc(db, SUITE_RESULTS, id).withConverter(converter));
    return snap.data();
  }

  async findBySuiteId(suiteId: string): Promise<SuiteResult[]> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const q = query(
      collection(db, SUITE_RESULTS),
      where('suiteId', '==', suiteId),
      where('environment', '==', getCurrentEnvironment())
    ).withConverter(converter);
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data());
  }

  async findByRunId(runId: string): Promise<SuiteResult[]> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const q = query(
      collection(db, SUITE_RESULTS),
      where('runId', '==', runId),
      where('environment', '==', getCurrentEnvironment())
    ).withConverter(converter);
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data());
  }

  async findByProjectId(projectId: string): Promise<SuiteResult[]> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const q = query(
      collection(db, SUITE_RESULTS),
      where('projectId', '==', projectId),
      where('environment', '==', getCurrentEnvironment())
    ).withConverter(converter);
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data());
  }

  async save(result: SuiteResult): Promise<SuiteResult> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const existing = await this.findById(result.id);
    const data = { ...result, environment: getCurrentEnvironment() };
    if (existing) {
      await updateDoc(doc(db, SUITE_RESULTS, result.id), data);
    } else {
      await setDoc(doc(db, SUITE_RESULTS, result.id).withConverter(converter), data);
    }
    return { ...data, createdAt: existing?.createdAt ?? new Date().toISOString() };
  }

  async delete(id: string): Promise<void> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    await deleteDoc(doc(db, SUITE_RESULTS, id));
  }
}

export const createFirestoreSuiteResultRepository = (): FirestoreSuiteResultRepository =>
  new FirestoreSuiteResultRepository();
